import { createContext, ChangeEvent, ReactNode, useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { Button, Form, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';

// ReactFlow
import ReactFlow, { Controls, Background, applyNodeChanges, applyEdgeChanges, addEdge, useReactFlow } from 'reactflow';
import '../../NodeDefinitionEditor.scss';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import api from 'services/api';
import EditStringWithHelp from 'components/edit/EditStringWithHelp';
import SearchBox from 'components/common/SearchBox';
import { Splitter } from 'components/Splitter/Splitter';
import StandardNode from './node_types/StandardNode';
import RoutedEdge, { ErrorRoutedEdge } from './edge_types/RoutedEdge';

import { fetchCustomerNodeDefinitions, fetchCustomerNodeDefinitionById } from '../../../../../../../redux/slices/customer_node_definitionsSlice';
import { fetchEvents } from '../../../../../../../redux/slices/eventsSlice';
import { getLocks } from '../../../../../../../redux/slices/locksSlice';
import { RootState } from '../../../../../../../redux/store';
import { LoadingSelect } from 'components/forms/LoadingFields';

import Queues from '../ui/Queues';
import ReadOnlyProperties from '../ui/ReadOnlyProperties';
import Schedules from '../ui/Schedules';
import ScheduleStatus from '../ui/ScheduleStatus';
import ScheduleSchedulingWindowSelect from '../ui/SchedulesSchedulingWindow';
import ScheduleScheduleStatus from '../ui/ScheduleSchedulingStatus';
import ScheduleStartTime from '../ui/ScheduleStartTime';
import SubflowGroupNode from './node_types/SubflowGroupNode';

export const SubflowNavigationContext = createContext<{
  navigateIntoSubflow: (nodeId: number, name: string) => void;
  isInsideSubflow: boolean;
  onExpandNode: ((nodeId: string) => void) | null;
} | null>(null);

const nodeTypes = {
  default: StandardNode,
  conditions: StandardNode,
  shell: StandardNode,
  bash: StandardNode, // backward compatibility
  flow: StandardNode,
  sap: StandardNode,
  sapibp: StandardNode,
  sapbw: StandardNode,
  rest: StandardNode,
  tableRead: StandardNode,
  sendMail: StandardNode,
  sql: StandardNode,
  sqlplus: StandardNode,
  workflowActionCreate: StandardNode,
  workflowCreate: StandardNode,
  subflowGroup: SubflowGroupNode,
  norscom_python: StandardNode,
};

const edgeTypes = {
  default: RoutedEdge,
  loopback: RoutedEdge,
  'error-loopback': ErrorRoutedEdge,
};

const SubflowBreadcrumbs = ({ items, onNavigate }) => (
  <div className="subflow-breadcrumbs">
    {items.map((item, i) => (
      <span key={i}>
        {i > 0 && <span className="breadcrumb-separator">›</span>}
        <span className={`breadcrumb-item ${i === items.length - 1 ? 'active' : ''}`} onClick={() => (i < items.length - 1 ? onNavigate(i) : null)}>
          {item.name}
        </span>
      </span>
    ))}
  </div>
);

// Pixel width limits for the two resizable side panes (the canvas always takes
// the remaining width). Fixed pixels — not percentages — so resizing or
// collapsing one side pane only changes the canvas, never the other pane, and
// the panes keep their width when the window resizes.
const FLOW_PANE_LIMITS: Record<'library' | 'panel', { min: number; max: number; default: number }> = {
  library: { min: 170, max: 520, default: 230 },
  panel: { min: 280, max: 640, default: 340 },
};

// Header bar shown at the top of a side pane: title + a chevron that collapses
// the pane to a rail, and an optional close (×) that dismisses the pane's
// content entirely (e.g. deselect the node). `side` decides which way the
// collapse chevron points.
const FlowPaneHeader = ({ title, side, onCollapse, onClose }: { title: string; side: 'left' | 'right'; onCollapse: () => void; onClose?: () => void }) => (
  <div className="flow-pane-header">
    <span className="flow-pane-title text-truncate" title={title}>
      {title}
    </span>
    <span className="flow-pane-header-actions">
      <button type="button" className="flow-pane-toggle" onClick={onCollapse} title="Collapse" aria-label={`Collapse ${title}`}>
        {side === 'left' ? '‹' : '›'}
      </button>
      {onClose && (
        <button type="button" className="flow-pane-toggle" onClick={onClose} title="Close" aria-label={`Close ${title}`}>
          ×
        </button>
      )}
    </span>
  </div>
);

// Thin rail a collapsed pane shrinks to: a chevron to expand + a vertical
// label. Clicking anywhere on the rail expands it.
const FlowPaneRail = ({ title, side, onExpand }: { title: string; side: 'left' | 'right'; onExpand: () => void }) => (
  <div
    className={`flow-pane-rail flow-pane-rail-${side}`}
    onClick={onExpand}
    role="button"
    tabIndex={0}
    title={`Expand ${title}`}
    aria-label={`Expand ${title}`}
  >
    <span className="flow-pane-toggle" aria-hidden>
      {side === 'left' ? '›' : '‹'}
    </span>
    <span className="flow-pane-rail-label">{title}</span>
  </div>
);

const RenderLibraryNodes = ({ state, onDragStart, onSearch, onSearchSubmit, searchValue, onLoadMore, hasMore, isLoading, nodes = [] }) => {
  const { t } = useTranslation();

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, onLoadMore]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onSearchSubmit();
    }
  };

  return (
    <>
      <div onKeyDown={handleSearchKeyDown}>
        <SearchBox placeholder={t('search') + '...'} size="sm" onChange={onSearch} value={searchValue} className="mb-4" style={{ width: '100%' }} />
      </div>
      {nodes.map((node, index) => {
        const { name, type, zone_name } = node;
        return (
          <div
            onDragStart={(event) => onDragStart(event, node)}
            data-type={type}
            data-name={name}
            data-zone={zone_name}
            key={`${node.node_definition_id}-${index}`}
            className="library-node"
            draggable
            // The name truncates in a narrow pane, so the hover title carries the
            // pair that identifies the definition.
            title={zone_name ? `${name} (${zone_name})` : name}
          >
            <span className="text">{name}</span>
            {/* Zone first: it is the discriminator between same-named definitions, and the
                ellipsis on this line eats the tail. Colliding definitions share a type. */}
            <span className="type">{zone_name ? `${zone_name} · ${type}` : type}</span>
          </div>
        );
      })}
      <div ref={sentinelRef} style={{ height: '20px', width: '100%' }}>
        {isLoading && (
          <div className="d-flex justify-content-center py-2">
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>
    </>
  );
};

const HANDLE_STATUS_OPTIONS = [
  { value: 'nothing', label: 'Nothing' },
  { value: 'raise_operator_message', label: 'Raise operator message' },
  { value: 'fail_flow', label: 'Fail flow' },
  { value: 'complete_flow', label: 'Complete flow' },
] as const;

const normalizeLockTags = (locks: unknown): string[] =>
  Array.isArray(locks) ? locks.map((lock) => (typeof lock === 'string' ? lock.trim() : '')).filter(Boolean) : [];

export const formatLockTag = (tag: string) => {
  const [zoneName, ...lockNameParts] = tag.split('|');
  const lockName = lockNameParts.join('|');
  return lockName ? `${lockName} (${zoneName})` : tag;
};

const lockTagsAreEqual = (a: string[], b: string[]) => a.length === b.length && a.every((tag, index) => tag === b[index]);

const updateParameterOverride = (parameters: Record<string, unknown>[] = [], name: string, updates: Record<string, unknown>) => {
  const nextParams = [...parameters];
  const index = nextParams.findIndex((parameter) => parameter.name === name);
  const nextParameter = index >= 0 ? { ...nextParams[index] } : { name };

  Object.entries(updates).forEach(([key, value]) => {
    if (value === undefined || value === null || ((key === 'value' || key === 'parent_parameter_out') && value === '')) {
      delete nextParameter[key];
    } else {
      nextParameter[key] = value;
    }
  });

  if (index >= 0) {
    nextParams[index] = nextParameter;
  } else {
    nextParams.push(nextParameter);
  }

  return nextParams.filter(
    (parameter) =>
      (Object.prototype.hasOwnProperty.call(parameter, 'value') && parameter.value !== undefined && parameter.value !== null && parameter.value !== '') ||
      Object.prototype.hasOwnProperty.call(parameter, 'runtime') ||
      (Object.prototype.hasOwnProperty.call(parameter, 'parent_parameter_out') &&
        parameter.parent_parameter_out !== undefined &&
        parameter.parent_parameter_out !== null &&
        parameter.parent_parameter_out !== '')
  );
};

const NODE_ID_REGEX = /^[a-zA-Z0-9_]+$/;

const RenderMiniEditor = ({
  state,
  onChange,
  onRuntimeChange,
  onParentParameterOutChange,
  onQueueChange,
  onWindowChange,
  onWindowStatusChange,
  onPreconditionExpressionChange,
  onScheduleSchedulingWindowChange,
  onScheduleSchedulingWindowStatusChange,
  onScheduleStartTimeChange,
  onSubmitDisabledChange,
  onSubmitAndForgetChange,
  onHandleStatusActionChange,
  onWaitEventAdd,
  onWaitEventRemove,
  onWaitEventChange,
  onRaiseEventAdd,
  onRaiseEventRemove,
  onRaiseEventChange,
  processEvents,
  isEventsLoading,
  onLockAdd,
  onLockRemove,
  onLockChange,
  availableLocks,
  isLocksLoading,
  onNodeIdChange,
  onLabelChange,
  onDescriptionChange,
}) => {
  const { t } = useTranslation();
  const { selectedNode = {}, child_nodes } = state;
  const { parameters = [], default_queue_path, queue_path, id } = selectedNode;
  const [editingNodeId, setEditingNodeId] = useState(id || '');
  const [nodeIdError, setNodeIdError] = useState('');

  useEffect(() => {
    setEditingNodeId(id || '');
    setNodeIdError('');
  }, [id]);

  const handleNodeIdBlur = () => {
    if (editingNodeId === id) return;
    if (!editingNodeId.trim()) {
      setNodeIdError('Node ID cannot be empty');
      return;
    }
    if (!NODE_ID_REGEX.test(editingNodeId)) {
      setNodeIdError('Only letters, numbers, and underscores allowed');
      return;
    }
    const isDuplicate = child_nodes.some((node) => node.id === editingNodeId && node.id !== id);
    if (isDuplicate) {
      setNodeIdError('Node ID already exists');
      return;
    }
    setNodeIdError('');
    onNodeIdChange(id, editingNodeId);
  };
  const currentChildNode = child_nodes.find((node) => node.id === id);
  const isSubmitAndForgetPlacement = currentChildNode?.submit_and_forget === true;
  const handlesRaw = selectedNode.handles;
  const handles = Array.isArray(handlesRaw)
    ? handlesRaw
    : typeof handlesRaw === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(handlesRaw);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })()
      : [];
  const selectedScheduleWindow = currentChildNode?.schedule_scheduling_window;
  const selectedScheduleWindowStatus = currentChildNode?.schedule_scheduling_window_status;
  const selectedScheduleStartTime = currentChildNode?.schedule_start_time ?? null;
  const selectedQueue = currentChildNode?.queue_path;
  const selectedWindow = currentChildNode?.precondition_scheduling_window;
  const selectedWindowStatus = currentChildNode?.precondition_scheduling_window_status;
  const preconditionExpression = currentChildNode?.precondition ?? '';
  const definitionLocks = normalizeLockTags(selectedNode.locks);
  const definitionLockSet = new Set(definitionLocks);
  const rawFlowLocks = currentChildNode?.locks || [];
  const flowLocks = normalizeLockTags(rawFlowLocks);
  const lockOptions = (availableLocks || []).map((lock: any) => ({
    tag: `${lock.zone_name}|${lock.name}`,
    label: `${lock.name} (${lock.zone_name})`,
  }));
  const selectableFlowLockTags = lockOptions.map((option) => option.tag).filter((tag) => !definitionLockSet.has(tag) && !flowLocks.includes(tag));
  const hasEmptyLockRow = rawFlowLocks.some((tag: string) => !tag);
  const canAddFlowLock = !isLocksLoading && !hasEmptyLockRow && selectableFlowLockTags.length > 0;
  const getFlowLockOptions = (currentTag: string, currentIndex: number) =>
    lockOptions
      .filter((option) => {
        const isCurrentValue = option.tag === currentTag;
        const isInherited = definitionLockSet.has(option.tag);
        const isSelectedByAnotherRow = rawFlowLocks.some((selectedTag: string, index: number) => index !== currentIndex && selectedTag === option.tag);

        return isCurrentValue || (!isInherited && !isSelectedByAnotherRow);
      })
      .map((option) => ({
        value: option.tag,
        label: definitionLockSet.has(option.tag) && option.tag === currentTag ? `${option.label} (${t('flow-editor.already-inherited')})` : option.label,
      }));
  const addLockButton = (
    <Button
      className="mt-2"
      size="sm"
      variant="outline-primary"
      onClick={() => onLockAdd(id)}
      disabled={!canAddFlowLock}
      style={!canAddFlowLock ? { pointerEvents: 'none' } : undefined}
    >
      {t('flow-editor.add-lock')}
    </Button>
  );

  let listOfParameters = parameters.map((parameter) => ({
    ...parameter,
    definitionRuntime: parameter.runtime,
    runtimeOverrideSet: false,
  }));

  if (currentChildNode?.parameters) {
    listOfParameters = parameters.map((currentParam) => {
      const matchingParam = currentChildNode.parameters.find((param) => param.name === currentParam.name);
      if (matchingParam) {
        const runtimeOverrideSet = Object.prototype.hasOwnProperty.call(matchingParam, 'runtime');
        return {
          ...currentParam,
          value: Object.prototype.hasOwnProperty.call(matchingParam, 'value') ? matchingParam.value : currentParam.value,
          parent_parameter_out: Object.prototype.hasOwnProperty.call(matchingParam, 'parent_parameter_out')
            ? matchingParam.parent_parameter_out
            : currentParam.parent_parameter_out,
          runtime: runtimeOverrideSet ? matchingParam.runtime === true || matchingParam.runtime === 'true' : currentParam.runtime,
          definitionRuntime: currentParam.runtime,
          runtimeOverrideSet,
          inout: currentParam.inout, // Keep the inout from the node definition
        };
      }
      return {
        ...currentParam,
        definitionRuntime: currentParam.runtime,
        runtimeOverrideSet: false,
      };
    });
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label>Node ID</Form.Label>
        <Form.Control
          type="text"
          value={editingNodeId}
          onChange={(e) => {
            setEditingNodeId(e.target.value);
            setNodeIdError('');
          }}
          onBlur={handleNodeIdBlur}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          isInvalid={!!nodeIdError}
        />
        {nodeIdError && <Form.Text className="text-danger">{nodeIdError}</Form.Text>}
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Label</Form.Label>
        <Form.Control
          type="text"
          value={currentChildNode?.label ?? ''}
          placeholder={selectedNode.name || ''}
          onChange={(e) => onLabelChange(e, id)}
          onKeyDown={handleKeyDown}
        />
      </Form.Group>
      {selectedNode.name && (
        <Form.Group className="mb-2">
          <Form.Label>Name</Form.Label>
          <Form.Control type="text" value={selectedNode.name} disabled />
        </Form.Group>
      )}
      <Form.Group className="mb-2">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={currentChildNode?.description ?? ''}
          placeholder={selectedNode.description || ''}
          onChange={(e) => onDescriptionChange(e, id)}
          onKeyDown={handleKeyDown}
        />
      </Form.Group>
      {listOfParameters &&
        listOfParameters.map((parameter, index) => {
          const { name, value, default_expression, inout, parent_parameter_out, definitionRuntime, runtime, runtimeOverrideSet } = parameter;
          const definitionRuntimeValue = definitionRuntime === true || definitionRuntime === 'true';
          const runtimeValue = runtime === true || runtime === 'true';
          const runtimeOverrideValue = runtimeOverrideSet ? String(runtimeValue) : 'inherit';
          const expressionValue = value !== undefined && value !== null && value !== '' ? value : default_expression;
          const showRuntimeWarning =
            !runtimeValue &&
            typeof expressionValue === 'string' &&
            expressionValue.trim().startsWith('=') &&
            /\bNode\.(Sibling|output|outputDetails)\b|\bSibbling_/i.test(expressionValue);

          return (
            <Form.Group key={`${name}-${index}-${id}`} className="flow-parameter-group mb-3" controlId={`${name}-${index}`}>
              <Form.Label className="fw-semibold mb-2">{name}</Form.Label>
              <Form.Group className="mb-2" controlId={`${name}-${index}-value`}>
                <Form.Label className="small mb-1">Value</Form.Label>
                <Form.Control
                  type="text"
                  name={name}
                  value={value ?? ''}
                  placeholder={!value ? default_expression : undefined}
                  onChange={(e) => onChange(e, name, id)}
                  onKeyDown={handleKeyDown}
                />
              </Form.Group>
              <Form.Group className="mb-2" controlId={`${name}-${index}-definition-runtime`}>
                <div className="d-flex align-items-center gap-2 small">
                  <span className="text-body-secondary">Definition runtime:</span>
                  <span className={`badge ${definitionRuntimeValue ? 'bg-success' : 'bg-secondary'}`}>{String(definitionRuntimeValue)}</span>
                </div>
              </Form.Group>
              <Form.Group className="mb-2" controlId={`${name}-${index}-runtime-override`}>
                <Form.Label className="small mb-1">Runtime override</Form.Label>
                <Form.Select
                  size="sm"
                  value={runtimeOverrideValue}
                  onChange={(e) => {
                    const nextRuntime = e.target.value;
                    onRuntimeChange(name, id, nextRuntime === 'inherit' ? undefined : nextRuntime === 'true');
                  }}
                >
                  <option value="inherit">Inherit from definition</option>
                  <option value="true">Override: true</option>
                  <option value="false">Override: false</option>
                </Form.Select>
                <Form.Text muted>Effective runtime: {String(runtimeValue)}</Form.Text>
                {showRuntimeWarning && (
                  <div className="flow-runtime-warning small mb-0 mt-2">
                    This expression may need runtime=true. With runtime=false it is evaluated when flow children are inserted, before sibling or output values
                    may exist.
                  </div>
                )}
              </Form.Group>
              {(inout === 'inout' || inout === 'out') && (
                <Form.Group className="mb-0" controlId={`${name}-${index}-parent-parameter-out`}>
                  <Form.Label className="small mb-1">Update parent parameter</Form.Label>
                  <Form.Control
                    type="text"
                    name={name}
                    value={parent_parameter_out ?? ''}
                    placeholder={''}
                    onChange={(e) => onParentParameterOutChange(e, name, id)}
                    onKeyDown={handleKeyDown}
                  />
                </Form.Group>
              )}
            </Form.Group>
          );
        })}

      <Queues selectedValue={selectedQueue} defaultValue={default_queue_path} onChange={(e) => onQueueChange(e, id)} />

      <Schedules selectedValue={selectedWindow} defaultValue={null} onChange={(e) => onWindowChange(e, id)} />

      {selectedWindow && <ScheduleStatus selectedValue={selectedWindowStatus} defaultValue={null} onChange={(e) => onWindowStatusChange(e, id)} />}

      <Form.Group className="mb-2" controlId="preconditionExpression">
        <Form.Label>Precondition expression</Form.Label>
        <EditStringWithHelp
          name="precondition_expression"
          value={preconditionExpression}
          placeholder='e.g. "{{test_flag}}" == "run"'
          onChange={(e) => onPreconditionExpressionChange(e, id)}
          onKeyDown={handleKeyDown}
          helpTitle="Precondition expression help"
        >
          <p className="mb-2">
            JEXL syntax. Use <code>==</code> / <code>!=</code> (not <code>===</code>).
          </p>
          <p className="mb-2">
            String example: <code>{'"{{test_flag}}" == "run"'}</code>
          </p>
          <p className="mb-0">
            Boolean/number examples: <code>{'{{is_enabled}} == true'}</code>, <code>{'{{amount}} > 0'}</code>
          </p>
        </EditStringWithHelp>
      </Form.Group>

      <ScheduleSchedulingWindowSelect
        selectedValue={selectedScheduleWindow ?? null}
        defaultValue={null}
        onChange={(e) => onScheduleSchedulingWindowChange(e, id)}
      />

      {selectedScheduleWindow && (
        <ScheduleScheduleStatus
          selectedValue={selectedScheduleWindowStatus ?? null}
          defaultValue="open"
          onChange={(e) => onScheduleSchedulingWindowStatusChange(e, id)}
        />
      )}

      <ScheduleStartTime selectedValue={selectedScheduleStartTime} onChange={(e) => onScheduleStartTimeChange(e, id)} />

      <Form.Group className="mt-3">
        <Form.Check
          type="checkbox"
          id={`submit-disabled-${id}`}
          label="Disabled by default"
          checked={currentChildNode?.submit_disabled === true}
          onChange={(e) => onSubmitDisabledChange(e, id)}
          title="This node will be automatically skipped when the flow runs"
        />
      </Form.Group>

      {onSubmitAndForgetChange && (
        <Form.Group className="mt-3">
          <Form.Check
            type="checkbox"
            id={`submit-and-forget-${id}`}
            label="Submit and forget"
            checked={isSubmitAndForgetPlacement}
            onChange={(e) => onSubmitAndForgetChange(e, id)}
            title="Detach-submit the referenced definition as a root instance; the flow step completes immediately"
          />
        </Form.Group>
      )}

      {handles.length > 0 && onHandleStatusActionChange && (
        <Form.Group className="mt-3">
          <Form.Label>Status handler (when flow reaches this node via handle)</Form.Label>
          {handles.map((handle: { id: string; key?: string; label?: string }) => (
            <Form.Group key={handle.id} className="mb-2">
              <Form.Label style={{ fontSize: '0.7em' }}>{handle.label ?? handle.key ?? handle.id}</Form.Label>
              <Form.Select
                value={(currentChildNode?.handle_status_actions as Record<string, string>)?.[handle.id] ?? 'nothing'}
                onChange={(e) => onHandleStatusActionChange(handle.id, e.target.value, id)}
              >
                {HANDLE_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ))}
        </Form.Group>
      )}

      {onWaitEventAdd && (
        <Form.Group className="mt-3">
          <Form.Label className="d-block mb-2">Wait Events</Form.Label>
          {(currentChildNode?.wait_events || []).map((event, index) => (
            <div key={index} className="d-flex align-items-center mb-2">
              <LoadingSelect
                name={`flow_wait_event_${id}_${index}`}
                value={event.path || ''}
                onChange={(e) => onWaitEventChange(id, index, 'path', e.target.value || null)}
                className="me-2"
                style={{ width: '200px' }}
                isLoading={isEventsLoading}
                options={(processEvents || []).map((ev: any) => ({
                  value: `${ev.zone_name}|${ev.name}`,
                  label: `${ev.name} (${ev.zone_name})`,
                }))}
                placeholder="Select event..."
                loadingLabel="Loading events..."
              />
              <Form.Check
                type="checkbox"
                label="Auto Clear"
                checked={event.auto_clear ?? true}
                onChange={(e) => onWaitEventChange(id, index, 'auto_clear', e.target.checked)}
                className="me-2"
              />
              <Form.Control
                type="text"
                value={event.precondition ?? ''}
                onChange={(e) => onWaitEventChange(id, index, 'precondition', e.target.value || null)}
                className="me-2"
                style={{ width: '160px' }}
                placeholder="Precondition: true or =expr"
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
              <Button variant="outline-secondary" size="sm" onClick={() => onWaitEventRemove(id, index)}>
                X
              </Button>
            </div>
          ))}
          <Button className="mt-2" size="sm" variant="outline-primary" onClick={() => onWaitEventAdd(id)}>
            Add wait event
          </Button>
        </Form.Group>
      )}

      {onRaiseEventAdd && !isSubmitAndForgetPlacement && (
        <Form.Group className="mt-3">
          <Form.Label className="d-block mb-2">Raise Events</Form.Label>
          {(currentChildNode?.raise_events || []).map((event, index) => (
            <div key={index} className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <LoadingSelect
                name={`flow_raise_event_${id}_${index}`}
                value={event.path || ''}
                onChange={(e) => onRaiseEventChange(id, index, 'path', e.target.value || null)}
                className="m-0"
                style={{ width: '160px' }}
                isLoading={isEventsLoading}
                options={(processEvents || []).map((ev: any) => ({
                  value: `${ev.zone_name}|${ev.name}`,
                  label: `${ev.name} (${ev.zone_name})`,
                }))}
                placeholder="Select event..."
                loadingLabel="Loading events..."
              />
              <Form.Control
                type="text"
                value={event.raiser_comment ?? ''}
                onChange={(e) => onRaiseEventChange(id, index, 'raiser_comment', e.target.value)}
                className="m-0"
                style={{ width: '220px' }}
                placeholder="Comment or =expression"
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
              <Form.Control
                type="text"
                value={event.precondition ?? ''}
                onChange={(e) => onRaiseEventChange(id, index, 'precondition', e.target.value)}
                className="m-0"
                style={{ width: '160px' }}
                placeholder="Precondition: true or =expr"
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
              <Button variant="outline-secondary" size="sm" onClick={() => onRaiseEventRemove(id, index)}>
                X
              </Button>
            </div>
          ))}
          <Button className="mt-2" size="sm" variant="outline-primary" onClick={() => onRaiseEventAdd(id)}>
            Add raise event
          </Button>
        </Form.Group>
      )}

      {definitionLocks.length > 0 && (
        <Form.Group className="mt-3">
          <Form.Label className="d-block mb-1">
            {t('flow-editor.definition-locks')} <small className="text-muted">({t('flow-editor.inherited-read-only')})</small>
          </Form.Label>
          {definitionLocks.map((tag: string, i: number) => (
            <div key={`${tag}-${i}`} className="mb-1">
              <span className="badge bg-light text-muted border">{formatLockTag(tag)}</span>
            </div>
          ))}
          <Form.Text className="text-muted d-block mt-1">{t('flow-editor.definition-locks-help')}</Form.Text>
        </Form.Group>
      )}

      {onLockAdd && !isSubmitAndForgetPlacement && (
        <Form.Group className="mt-3">
          <Form.Label className="d-block mb-2">
            {t('flow-editor.flow-node-locks')} <small className="text-muted">({t('flow-editor.additive')})</small>
          </Form.Label>
          {(currentChildNode?.locks || []).map((tag: string, index: number) => (
            <div key={index} className="d-flex align-items-center mb-2">
              <LoadingSelect
                name={`flow_lock_${id}_${index}`}
                value={tag || ''}
                onChange={(e) => onLockChange(id, index, e.target.value || null)}
                className="me-2"
                style={{ width: '200px' }}
                isLoading={isLocksLoading}
                options={getFlowLockOptions(tag, index)}
                placeholder="Select lock..."
                loadingLabel="Loading locks..."
              />
              <Button variant="outline-secondary" size="sm" onClick={() => onLockRemove(id, index)}>
                X
              </Button>
            </div>
          ))}
          <Form.Text className="text-muted d-block">{t('flow-editor.flow-node-locks-additive-help')}</Form.Text>
          {canAddFlowLock ? (
            addLockButton
          ) : (
            <OverlayTrigger placement="top" overlay={<Tooltip id={`flow-node-locks-unavailable-${id}`}>{t('flow-editor.no-locks-left')}</Tooltip>}>
              <span className="d-inline-block">{addLockButton}</span>
            </OverlayTrigger>
          )}
        </Form.Group>
      )}
    </>
  );
};

const normalizeChildNode = (node: any) => ({
  ...node,
  submit_disabled: node.submit_disabled === true,
  submit_and_forget: node.submit_and_forget === true,
  data: {
    ...node.data,
    submit_disabled: node.submit_disabled === true,
    submit_and_forget: node.submit_and_forget === true,
  },
});

const mapFetchedChildNodes = (childNodes: any[] = []) =>
  childNodes.map((node) => {
    const extractedZoneName = node.node_definition_path?.split('|')[0];
    const scheduleStartTime = typeof node.schedule_start_time === 'string' ? node.schedule_start_time.substring(0, 5) : (node.schedule_start_time ?? null);
    return normalizeChildNode({
      ...node,
      schedule_start_time: scheduleStartTime,
      node_definition_name: node.node_definition_name || node.name,
      zone_name: node.zone_name || extractedZoneName,
      submit_and_forget: node.submit_and_forget === true,
      data: {
        label: node.label || node.node_definition_name || node.name,
        handles: node.handles,
        description: node.data?.description || node.description || null,
        submit_and_forget: node.submit_and_forget === true,
      },
      description: node.flow_description ?? null,
    });
  });

const Flow = ({
  state,
  onChange,
  setState,
  onNodeDoubleClickHandler = null,
  showNodeLibrary = true,
  disabled = false,
  onSubflowStateChange = null,
  onNavChange = null,
  onFetchSubflow = null,
  onExpandNode = null,
}) => {
  const { child_nodes = [], child_edges = [] } = state;
  const { fitView } = useReactFlow();

  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const minimumSearchLength = 3;
  const searchTimeoutRef = useRef<number | null>(null);
  const reactFlowInstance = useReactFlow();

  const [searchInput, setSearchInput] = useState('');

  // Collapse state for the resizable side panes (rail vs full pane).
  const [libraryCollapsed, setLibraryCollapsed] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  // Side-pane widths in pixels; the canvas (flex:1) takes the rest.
  const [libraryWidth, setLibraryWidth] = useState(FLOW_PANE_LIMITS.library.default);
  const [panelWidth, setPanelWidth] = useState(FLOW_PANE_LIMITS.panel.default);

  // Selecting a (new) node should reveal its properties even if the panel was
  // left collapsed from a previous interaction.
  const selectedNodeId = state.selectedNode?.id;
  useEffect(() => {
    if (selectedNodeId) setPanelCollapsed(false);
  }, [selectedNodeId]);

  // Events for flow child node wait/raise
  const eventsState = useSelector((reduxState: RootState) => reduxState.events);
  const allEvents = eventsState.items || [];
  const isEventsLoading = eventsState.status === 'loading' || eventsState.loading.fetchEvents;
  const processEvents = allEvents.filter((event) => event.type === 'processEvent');

  useEffect(() => {
    if (!showNodeLibrary) return;
    dispatch(fetchEvents({ limit: 100 }) as any);
    dispatch(getLocks() as any);
  }, [dispatch, showNodeLibrary]);

  // Locks available for flow-node assignment, filtered to the flow's zone
  const locksState = useSelector((reduxState: RootState) => reduxState.locks);
  const allLocks = locksState.locks || [];
  const isLocksLoading = locksState.loading?.getLocks === true;
  const flowZoneName = state.zone_name;
  const availableLocks = flowZoneName ? allLocks.filter((lock: any) => lock.zone_name === flowZoneName) : allLocks;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [libraryNodes, setLibraryNodes] = useState<any[]>([]);
  const [definitionLocksByNodeDefinitionId, setDefinitionLocksByNodeDefinitionId] = useState<Record<string, string[]>>({});

  const hasMore = currentPage < totalPages;

  interface SubflowLevel {
    name: string;
    node_definition_id: number;
    child_nodes: any[];
    child_edges: any[];
  }

  const [subflowStack, setSubflowStack] = useState<SubflowLevel[]>([]);
  const [isNavigating, setIsNavigating] = useState(false);

  const isInsideSubflow = subflowStack.length > 0;
  const currentSubflow = isInsideSubflow ? subflowStack[subflowStack.length - 1] : null;
  const displayedNodes = currentSubflow ? currentSubflow.child_nodes : child_nodes;
  const displayedEdges = currentSubflow ? currentSubflow.child_edges : child_edges;

  const rememberDefinitionLocks = useCallback((nodeDefinitionId: number | string | null | undefined, locks: unknown) => {
    if (!nodeDefinitionId) return;
    const key = String(nodeDefinitionId);
    const normalizedLocks = normalizeLockTags(locks);

    setDefinitionLocksByNodeDefinitionId((prev) => {
      if (prev[key] !== undefined && lockTagsAreEqual(prev[key], normalizedLocks)) {
        return prev;
      }
      return { ...prev, [key]: normalizedLocks };
    });
  }, []);

  const displayedNodeDefinitionIds = useMemo<string[]>(() => {
    const ids = displayedNodes
      .map((node) => (node.node_definition_id != null ? String(node.node_definition_id) : null))
      .filter((nodeDefinitionId): nodeDefinitionId is string => Boolean(nodeDefinitionId));

    return Array.from(new Set(ids));
  }, [displayedNodes]);

  useEffect(() => {
    let isCancelled = false;
    const missingNodeDefinitionIds = displayedNodeDefinitionIds.filter((nodeDefinitionId) => definitionLocksByNodeDefinitionId[nodeDefinitionId] === undefined);

    if (missingNodeDefinitionIds.length === 0) return;

    missingNodeDefinitionIds.forEach(async (nodeDefinitionId) => {
      try {
        const response = await api.get(`/node_definitions/${nodeDefinitionId}`);
        if (isCancelled) return;
        rememberDefinitionLocks(nodeDefinitionId, response?.data?.locks);
      } catch (error) {
        if (!isCancelled) {
          rememberDefinitionLocks(nodeDefinitionId, []);
        }
        console.error('Error fetching node definition locks:', error);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [definitionLocksByNodeDefinitionId, displayedNodeDefinitionIds, rememberDefinitionLocks]);

  const nodesWithDefinitionLocks = useMemo(
    () =>
      displayedNodes.map((node) => {
        const nodeDefinitionId = node.node_definition_id;
        const knownLocks = nodeDefinitionId != null ? definitionLocksByNodeDefinitionId[String(nodeDefinitionId)] : undefined;
        const currentLocks = normalizeLockTags(node.data?.definition_locks);
        const nextLocks = knownLocks ?? currentLocks;

        if (lockTagsAreEqual(currentLocks, nextLocks)) return node;

        return {
          ...node,
          data: {
            ...node.data,
            definition_locks: nextLocks,
          },
        };
      }),
    [definitionLocksByNodeDefinitionId, displayedNodes]
  );
  const isReadOnlyViewer = disabled && !!onNodeDoubleClickHandler;
  const showLibrary = showNodeLibrary && !isInsideSubflow;
  const showPropertyPanel = !!state.selectedNode && !isInsideSubflow;
  const libraryExpanded = showLibrary && !libraryCollapsed;
  const panelExpanded = showPropertyPanel && !panelCollapsed;
  const selectedNodeTitle = state.selectedNode?.name ? `Properties - ${state.selectedNode.name}` : 'Properties';

  // Notify parent when subflow state changes
  useEffect(() => {
    if (onSubflowStateChange) onSubflowStateChange(isInsideSubflow);
  }, [isInsideSubflow, onSubflowStateChange]);

  const breadcrumbItems = useMemo(() => [{ name: state.name || 'Root' }, ...subflowStack.map((level) => ({ name: level.name }))], [state.name, subflowStack]);

  const navigateIntoSubflow = useCallback(
    async (nodeId: number, nodeName: string) => {
      setIsNavigating(true);
      setState((prev) => ({ ...prev, selectedNode: null }));

      try {
        let childNodes;
        let childEdges;

        if (onFetchSubflow) {
          const result = await onFetchSubflow(nodeId);
          if (result) {
            childNodes = result.child_nodes;
            childEdges = result.child_edges;
          }
        } else {
          const response: any = await dispatch(fetchCustomerNodeDefinitionById(nodeId));
          if (response?.payload) {
            childNodes = mapFetchedChildNodes(response.payload.child_nodes);
            childEdges = response.payload.child_edges || [];
          }
        }

        if (childNodes) {
          setSubflowStack((prev) => [
            ...prev,
            {
              name: nodeName,
              node_definition_id: nodeId,
              child_nodes: childNodes,
              child_edges: childEdges || [],
            },
          ]);

          setTimeout(() => fitView(), 100);
        }
      } catch (error) {
        console.error('Failed to navigate into subflow:', error);
      } finally {
        setIsNavigating(false);
      }
    },
    [dispatch, setState, fitView, onFetchSubflow]
  );

  const navigateToBreadcrumb = useCallback(
    (index: number) => {
      setState((prev) => ({ ...prev, selectedNode: null }));

      if (index === 0) {
        setSubflowStack([]);
      } else {
        setSubflowStack((prev) => prev.slice(0, index));
      }

      setTimeout(() => fitView(), 100);
    },
    [setState, fitView]
  );

  const navigateBack = useCallback(() => {
    setState((prev) => ({ ...prev, selectedNode: null }));
    setSubflowStack((prev) => prev.slice(0, -1));
    setTimeout(() => fitView(), 100);
  }, [setState, fitView]);

  const subflowNavContextValue = useMemo(
    () => ({
      navigateIntoSubflow,
      isInsideSubflow,
      onExpandNode: onExpandNode || null,
    }),
    [navigateIntoSubflow, isInsideSubflow, onExpandNode]
  );

  useEffect(() => {
    if (!onNavChange) return;
    if (!isInsideSubflow) {
      onNavChange(null);
      return;
    }
    onNavChange(
      <div className="subflow-nav-bar d-inline-flex align-items-center ms-3">
        <Button variant="outline-secondary" size="sm" onClick={navigateBack} className="me-3">
          ← Back
        </Button>
        <SubflowBreadcrumbs items={breadcrumbItems} onNavigate={navigateToBreadcrumb} />
      </div>
    );
  }, [isInsideSubflow, breadcrumbItems, navigateBack, navigateToBreadcrumb, onNavChange]);

  const onSubflowNodesChange = useCallback((changes) => {
    setSubflowStack((prev) => {
      if (prev.length === 0) return prev;
      const stack = [...prev];
      const last = stack[stack.length - 1];
      stack[stack.length - 1] = {
        ...last,
        child_nodes: applyNodeChanges(changes, last.child_nodes),
      };
      return stack;
    });
  }, []);

  const onSubflowEdgesChange = useCallback((changes) => {
    setSubflowStack((prev) => {
      if (prev.length === 0) return prev;
      const stack = [...prev];
      const last = stack[stack.length - 1];
      stack[stack.length - 1] = {
        ...last,
        child_edges: applyEdgeChanges(changes, last.child_edges),
      };
      return stack;
    });
  }, []);

  const fetchNodes = useCallback(
    async (page: number) => {
      return dispatch(
        fetchCustomerNodeDefinitions({
          limit: 15,
          type: 'All',
          sortBy: 'node_definition_id',
          search: searchTerm,
          sortOrder: 'asc',
          page,
        })
      );
    },
    [dispatch, searchTerm]
  );

  useEffect(() => {
    if (!showNodeLibrary) return;

    const getNodeLibrary = async () => {
      try {
        const response: any = await fetchNodes(1);

        if (response?.payload) {
          setLibraryNodes(response.payload.nodes || []);
          setTotalPages(response.payload.pagination?.total_pages || 1);
          setCurrentPage(1);
        }
      } catch (error) {
        console.error('Error fetching node definitions:', error);
      }
    };
    getNodeLibrary();
  }, [fetchNodes, showNodeLibrary]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const response: any = await fetchNodes(nextPage);

      if (response?.payload?.nodes) {
        setLibraryNodes((prev) => [...prev, ...response.payload.nodes]);
        setCurrentPage(nextPage);
      }
    } catch (error) {
      console.error('Error fetching more node definitions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, fetchNodes, hasMore, isLoadingMore]);

  const styles = {
    width: '100%',
    height: '100%',
  };

  // ReactFlow Handlers
  const onNodeDoubleClick = useCallback(
    async (_e, node) => {
      const currentNodeId = node?.id;
      if (!currentNodeId) return;

      const node_definition_id = node?.node_definition_id;
      if (!node_definition_id) return;

      try {
        const response: any = await dispatch(fetchCustomerNodeDefinitionById(node_definition_id));

        const currentNodeDetails = response?.payload;
        if (!currentNodeDetails) return;

        rememberDefinitionLocks(node_definition_id, currentNodeDetails.locks);

        setState((prev) => ({
          ...prev,
          selectedNode: { ...currentNodeDetails, id: currentNodeId },
        }));
      } catch (error) {
        console.error('Error fetching node definitions:', error);
      }
    },
    [dispatch, rememberDefinitionLocks, setState]
  );

  const onConnect = useCallback(
    (params) => {
      return setState((prev) => {
        const cleanEdgeId = (edgeId) => (edgeId.startsWith('reactflow__edge-') ? edgeId.replace('reactflow__edge-', '') : edgeId);
        const id = cleanEdgeId(uuidv4()); // use UUID without `reactflow__edge-` prefix

        const sourceNode = reactFlowInstance.getNode(params.source);
        const targetNode = reactFlowInstance.getNode(params.target);

        const isTargetLeftOfSource = targetNode?.position.x < sourceNode?.position.x;
        const edgeType = isTargetLeftOfSource ? 'loopback' : 'default';
        const newEdge = {
          id,
          source: params.source,
          target: params.target,
          sourceHandle: params.sourceHandle, // Store the source handle ID
          targetHandle: params.targetHandle, // Store the target handle ID
          type: edgeType,
        };

        const addedEdges = addEdge(newEdge, prev.child_edges);

        return {
          ...prev,
          child_edges: addedEdges,
        };
      });
    },
    [reactFlowInstance, setState]
  );

  const onNodesChange = useCallback(
    (changes) => {
      return setState((prev) => {
        const changedNodes = applyNodeChanges(changes, prev.child_nodes);
        return {
          ...prev,
          child_nodes: changedNodes,
        };
      });
    },
    [setState]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      return setState((prev) => {
        const changedEdges = applyEdgeChanges(changes, prev.child_edges);
        return {
          ...prev,
          child_edges: changedEdges,
        };
      });
    },
    [setState]
  );

  const onDrop = (event) => {
    const id = 0;
    const { screenToFlowPosition } = reactFlowInstance;
    const { child_nodes } = state;
    const selectedNode = JSON.parse(event.dataTransfer.getData('application/reactflow'));
    const { type, name, description, handles, zone_name, hold_on_submit, node_definition_id } = selectedNode;
    const definitionLocks = normalizeLockTags(selectedNode.locks);

    event.preventDefault();

    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    let max = child_nodes.reduce((acc, value) => {
      const idNum = parseInt(value.id.replace('node-', ''), 10);
      return idNum > acc ? idNum : acc;
    }, 0);
    // Generate a unique ID for the new node
    max += 1;

    const newNodeId = `node-${max}`;
    const newNode = normalizeChildNode({
      id: newNodeId,
      node_definition_id,
      node_definition_name: name,
      position,
      zone_name,
      type,
      hold_on_submit: !!hold_on_submit,
      data: {
        label: name,
        handles,
        description: description || null,
        definition_locks: definitionLocks,
      },
      label: null,
      description: null,
      precondition: null,
      precondition_scheduling_window: null,
      precondition_scheduling_window_status: null,
      schedule_scheduling_window: null,
      schedule_scheduling_window_status: null,
      schedule_start_time: null,
    });

    rememberDefinitionLocks(node_definition_id, definitionLocks);

    return setState((prev) => ({
      ...prev,
      child_nodes: child_nodes.concat(newNode),
    }));
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const onDragStart = (event, nodeType) => {
    return event.dataTransfer.setData('application/reactflow', JSON.stringify(nodeType));
  };

  // Click handlers
  const handleChildNodeChange = (event: ChangeEvent<HTMLInputElement>, name, nodeId) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      const { parameters = [] } = node;

      if (node.id === nodeId) {
        return {
          ...node,
          parameters: updateParameterOverride(parameters, name, { value }),
        };
      }

      return node;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleRuntimeChange = (name, nodeId, runtime) => {
    const mappedNodes = child_nodes.map((node) => {
      const { parameters = [] } = node;

      if (node.id === nodeId) {
        return {
          ...node,
          parameters: updateParameterOverride(parameters, name, { runtime }),
        };
      }

      return node;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleNodeIdChange = (oldId: string, newId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id === oldId) {
        return { ...node, id: newId, data: { ...node.data, id: newId } };
      }
      return node;
    });

    const mappedEdges = child_edges.map((edge) => {
      let updated = edge;
      if (edge.source === oldId) {
        updated = { ...updated, source: newId };
      }
      if (edge.target === oldId) {
        updated = { ...updated, target: newId };
      }
      return updated;
    });

    setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
      child_edges: mappedEdges,
      selectedNode: prev.selectedNode?.id === oldId ? { ...prev.selectedNode, id: newId } : prev.selectedNode,
    }));
  };

  const handleLabelChange = (event: ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      return {
        ...node,
        label: value || null,
        data: { ...node.data, label: value || node.node_definition_name },
      };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleParentParameterOutChange = (event: ChangeEvent<HTMLInputElement>, name, nodeId) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      const { parameters = [] } = node;

      if (node.id === nodeId) {
        return {
          ...node,
          parameters: updateParameterOverride(parameters, name, {
            parent_parameter_out: value,
          }),
        };
      }

      return node;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleQueueChange = (event: ChangeEvent<HTMLInputElement>, nodeId) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id === nodeId) {
        return { ...node, queue_path: value || null };
      }
      return node;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      return { ...node, description: value || null };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleHandleStatusActionChange = (handleId: string, value: string, nodeId: string) => {
    const action = value === 'nothing' ? null : (value as 'raise_operator_message' | 'fail_flow' | 'complete_flow');
    const mappedNodes = child_nodes.map((node) => {
      if (node.id === nodeId) {
        const current = node.handle_status_actions || {};
        const next = action === null ? { ...current, [handleId]: undefined } : { ...current, [handleId]: value };
        const cleaned = Object.fromEntries(Object.entries(next).filter(([, v]) => v != null)) as Record<string, string>;
        return {
          ...node,
          handle_status_actions: Object.keys(cleaned).length > 0 ? cleaned : undefined,
        };
      }
      return node;
    });
    setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleSearchChange = (eOrValue: ChangeEvent<HTMLInputElement> | string) => {
    const value = typeof eOrValue === 'string' ? eOrValue : eOrValue.target.value;

    setSearchInput(value);

    const next = value.length >= minimumSearchLength ? value : '';
    if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = window.setTimeout(() => {
      setSearchTerm(next);
    }, 300);
  };

  const handleSearchSubmit = useCallback(() => {
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearchTerm(searchInput);
  }, [searchInput]);

  const handleWindowChange = (event: ChangeEvent<HTMLSelectElement>, nodeId: string) => {
    const { value } = event.target;
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      const hasWindow = Boolean(value);
      const next: any = {
        ...node,
        precondition_scheduling_window: hasWindow ? value : null,
      };

      if (hasWindow) {
        next.precondition_scheduling_window_status = node.precondition_scheduling_window_status ?? 'open';
      } else {
        next.precondition_scheduling_window_status = null;
      }

      return next;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleWindowStatusChange = (event: ChangeEvent<HTMLSelectElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      if (!node.precondition_scheduling_window) {
        return { ...node, precondition_scheduling_window_status: null };
      }

      return {
        ...node,
        precondition_scheduling_window_status: value || null,
      };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handlePreconditionExpressionChange = (event: ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      return {
        ...node,
        precondition: value.trim() ? value : null,
      };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleScheduleSchedulingWindowChange = (event: React.ChangeEvent<HTMLSelectElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      const hasWindow = Boolean(value);
      const next: any = {
        ...node,
        schedule_scheduling_window: hasWindow ? value : null,
      };

      if (hasWindow) {
        next.schedule_scheduling_window_status = node.schedule_scheduling_window_status ?? 'open';
      } else {
        next.schedule_scheduling_window_status = null;
      }

      return next;
    });

    return setState((prev: any) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleScheduleSchedulingWindowStatusChange = (event: React.ChangeEvent<HTMLSelectElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      if (!node.schedule_scheduling_window) {
        return { ...node, schedule_scheduling_window_status: null };
      }

      return {
        ...node,
        schedule_scheduling_window_status: value || null,
      };
    });

    return setState((prev: any) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleScheduleStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      return {
        ...node,
        schedule_start_time: value || null,
      };
    });

    return setState((prev: any) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleSubmitDisabledChange = (event: React.ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { checked } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      return {
        ...node,
        submit_disabled: checked,
        data: { ...(node.data || {}), submit_disabled: checked },
      };
    });

    return setState((prev: any) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleSubmitAndForgetChange = (event: React.ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { checked } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      return normalizeChildNode({
        ...node,
        submit_and_forget: checked,
        ...(checked ? { raise_events: [], locks: [] } : {}),
      });
    });

    return setState((prev: any) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Wait event handlers
  const handleWaitEventAdd = (nodeId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const waitEvents = node.wait_events || [];
      return {
        ...node,
        wait_events: [...waitEvents, { path: null, auto_clear: true, precondition: null }],
      };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleWaitEventRemove = (nodeId: string, eventIndex: number) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const waitEvents = [...(node.wait_events || [])];
      waitEvents.splice(eventIndex, 1);
      return { ...node, wait_events: waitEvents };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleWaitEventChange = (nodeId: string, eventIndex: number, field: string, value: any) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const waitEvents = [...(node.wait_events || [])];
      waitEvents[eventIndex] = { ...waitEvents[eventIndex], [field]: value };
      return { ...node, wait_events: waitEvents };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  // Raise event handlers
  const handleRaiseEventAdd = (nodeId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const raiseEvents = node.raise_events || [];
      return {
        ...node,
        raise_events: [...raiseEvents, { path: null, raiser_comment: '', precondition: '' }],
      };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleRaiseEventRemove = (nodeId: string, eventIndex: number) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const raiseEvents = [...(node.raise_events || [])];
      raiseEvents.splice(eventIndex, 1);
      return { ...node, raise_events: raiseEvents };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleRaiseEventChange = (nodeId: string, eventIndex: number, field: string, value: any) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const raiseEvents = [...(node.raise_events || [])];
      raiseEvents[eventIndex] = { ...raiseEvents[eventIndex], [field]: value };
      return { ...node, raise_events: raiseEvents };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  // Lock handlers
  const handleLockAdd = (nodeId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const locks = node.locks || [];
      return { ...node, locks: [...locks, ''] };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleLockRemove = (nodeId: string, lockIndex: number) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const locks = [...(node.locks || [])];
      locks.splice(lockIndex, 1);
      return { ...node, locks };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleLockChange = (nodeId: string, lockIndex: number, value: string | null) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const locks = [...(node.locks || [])];
      locks[lockIndex] = value || '';
      return { ...node, locks };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleCloseEditor = () => {
    return setState({ ...state, selectedNode: null });
  };

  const effectiveDisabled = disabled || isInsideSubflow;

  const libraryPane = (
    <div key="library" className="flow-pane node-library-pane" style={{ width: libraryWidth }}>
      <FlowPaneHeader title="Node Library" side="left" onCollapse={() => setLibraryCollapsed(true)} />
      <div className="flow-pane-body node-library">
        <RenderLibraryNodes
          state={state}
          onDragStart={onDragStart}
          onSearch={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          searchValue={searchInput}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          isLoading={isLoadingMore}
          nodes={libraryNodes}
        />
      </div>
    </div>
  );

  const canvasPane = (
    <div key="canvas" className="flow-pane canvas-pane">
      {!effectiveDisabled && (
        <div className="flow-pane-header">
          <span className="flow-pane-title">Flow edit</span>
        </div>
      )}

      {isNavigating && (
        <div className="subflow-loading-overlay">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      <div
        id="reactflow-wrapper"
        className="flow-pane-canvas"
        onDrop={isInsideSubflow ? undefined : onDrop}
        onDragOver={isInsideSubflow ? undefined : onDragOver}
      >
        <ReactFlow
          style={styles}
          nodes={
            onExpandNode
              ? nodesWithDefinitionLocks.map((n) => ({
                  ...n,
                  data: { ...n.data, onExpandNode },
                }))
              : nodesWithDefinitionLocks
          }
          edges={displayedEdges}
          onNodesChange={isInsideSubflow ? onSubflowNodesChange : onNodesChange}
          onEdgesChange={isInsideSubflow ? onSubflowEdgesChange : onEdgesChange}
          onNodeDoubleClick={isInsideSubflow ? undefined : onNodeDoubleClickHandler || onNodeDoubleClick}
          onNodeClick={isInsideSubflow || !isReadOnlyViewer ? undefined : onNodeDoubleClickHandler}
          onConnect={isInsideSubflow ? undefined : onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          proOptions={{ hideAttribution: true }}
          fitView
        >
          <Background />
          <Controls position="top-left" />
        </ReactFlow>
      </div>
    </div>
  );

  const propertyPane = (
    <div key="panel" className="flow-pane mini-editor-pane" style={{ width: panelWidth }}>
      <FlowPaneHeader title={selectedNodeTitle} side="right" onCollapse={() => setPanelCollapsed(true)} onClose={handleCloseEditor} />
      <div className="flow-pane-body mini-editor">
        {disabled ? (
          <ReadOnlyProperties state={state.selectedNode} onClose={handleCloseEditor} />
        ) : (
          <RenderMiniEditor
            state={state}
            onChange={handleChildNodeChange}
            onRuntimeChange={handleRuntimeChange}
            onParentParameterOutChange={handleParentParameterOutChange}
            onQueueChange={handleQueueChange}
            onWindowChange={handleWindowChange}
            onWindowStatusChange={handleWindowStatusChange}
            onPreconditionExpressionChange={handlePreconditionExpressionChange}
            onScheduleSchedulingWindowChange={handleScheduleSchedulingWindowChange}
            onScheduleSchedulingWindowStatusChange={handleScheduleSchedulingWindowStatusChange}
            onScheduleStartTimeChange={handleScheduleStartTimeChange}
            onSubmitDisabledChange={handleSubmitDisabledChange}
            onSubmitAndForgetChange={handleSubmitAndForgetChange}
            onHandleStatusActionChange={handleHandleStatusActionChange}
            onWaitEventAdd={handleWaitEventAdd}
            onWaitEventRemove={handleWaitEventRemove}
            onWaitEventChange={handleWaitEventChange}
            onRaiseEventAdd={handleRaiseEventAdd}
            onRaiseEventRemove={handleRaiseEventRemove}
            onRaiseEventChange={handleRaiseEventChange}
            processEvents={processEvents}
            isEventsLoading={isEventsLoading}
            onLockAdd={handleLockAdd}
            onLockRemove={handleLockRemove}
            onLockChange={handleLockChange}
            availableLocks={availableLocks}
            isLocksLoading={isLocksLoading}
            onNodeIdChange={handleNodeIdChange}
            onLabelChange={handleLabelChange}
            onDescriptionChange={handleDescriptionChange}
          />
        )}
      </div>
    </div>
  );

  // Flex row of keyed children: [library rail | library pane + handle] · canvas
  // · [handle + property pane | property rail]. Keys keep the canvas (and its
  // ReactFlow instance) mounted across collapse/expand/select, so the viewport
  // is preserved without any extra bookkeeping.
  const flowRowChildren: ReactNode[] = [];

  if (showLibrary && libraryCollapsed) {
    flowRowChildren.push(<FlowPaneRail key="library-rail" side="left" title="Node Library" onExpand={() => setLibraryCollapsed(false)} />);
  }
  if (libraryExpanded) {
    flowRowChildren.push(libraryPane);
    flowRowChildren.push(
      <Splitter
        key="library-resizer"
        size={libraryWidth}
        onResize={setLibraryWidth}
        min={FLOW_PANE_LIMITS.library.min}
        max={FLOW_PANE_LIMITS.library.max}
        side="before"
        ariaLabel="Resize node library"
      />
    );
  }

  flowRowChildren.push(canvasPane);

  if (panelExpanded) {
    flowRowChildren.push(
      <Splitter
        key="panel-resizer"
        size={panelWidth}
        onResize={setPanelWidth}
        min={FLOW_PANE_LIMITS.panel.min}
        max={FLOW_PANE_LIMITS.panel.max}
        side="after"
        ariaLabel="Resize property panel"
      />
    );
    flowRowChildren.push(propertyPane);
  }
  if (showPropertyPanel && panelCollapsed) {
    flowRowChildren.push(<FlowPaneRail key="panel-rail" side="right" title={selectedNodeTitle} onExpand={() => setPanelCollapsed(false)} />);
  }

  return (
    <SubflowNavigationContext.Provider value={subflowNavContextValue}>
      <div className="react-flow-container flow-split-row d-flex">{flowRowChildren}</div>
    </SubflowNavigationContext.Provider>
  );
};

export default Flow;
